import React, { useEffect, useState } from "react";
import api, { photoUrl } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Image as ImageIcon, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";

const empty = {
  name: "",
  website: "",
  contact_email: "",
  contact_phone: "",
  notes: "",
  logo_photo_id: null,
  search_mode: "streamlined",
};

export default function ManufacturerFormDialog({
  open,
  onOpenChange,
  mode = "create",
  manufacturer,
  onSaved,
}) {
  const [form, setForm] = useState(empty);
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && manufacturer) {
      setForm({
        name: manufacturer.name || "",
        website: manufacturer.website || "",
        contact_email: manufacturer.contact_email || "",
        contact_phone: manufacturer.contact_phone || "",
        notes: manufacturer.notes || "",
        logo_photo_id: manufacturer.logo_photo_id || null,
        search_mode: manufacturer.search_mode || "streamlined",
      });
    } else {
      setForm(empty);
    }
    setLogoFile(null);
  }, [open, mode, manufacturer]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let logo_photo_id = form.logo_photo_id;
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        const up = await api.post("/photos", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        logo_photo_id = up.data.id;
      }
      const payload = { ...form, logo_photo_id };
      let data;
      if (mode === "edit" && manufacturer) {
        const r = await api.patch(`/manufacturers/${manufacturer.id}`, payload);
        data = r.data;
        toast.success("Manufacturer updated");
      } else {
        const r = await api.post("/manufacturers", payload);
        data = r.data;
        toast.success("Manufacturer added");
      }
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeLogo = () => {
    setForm({ ...form, logo_photo_id: null });
    setLogoFile(null);
  };

  const previewSrc = logoFile
    ? URL.createObjectURL(logoFile)
    : form.logo_photo_id
    ? photoUrl(form.logo_photo_id)
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">
            {mode === "edit" ? "Edit manufacturer" : "Add manufacturer"}
          </DialogTitle>
          <DialogDescription>
            {mode === "edit"
              ? "Update the manufacturer's details and logo."
              : "Create a manufacturer. You can add product types and products inside it next."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4" data-testid="manufacturer-form">
          <div>
            <Label className="text-xs uppercase tracking-wider">Name *</Label>
            <Input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1.5 rounded-sm"
              placeholder="e.g. Acme Lighting"
              data-testid="manufacturer-name-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Website</Label>
              <Input
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                className="mt-1.5 rounded-sm"
                placeholder="acmelighting.com"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Contact phone</Label>
              <Input
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                className="mt-1.5 rounded-sm"
                placeholder="555-555-5555"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Contact email</Label>
            <Input
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              className="mt-1.5 rounded-sm"
              placeholder="rep@acmelighting.com"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Logo</Label>
            <div className="mt-1.5 flex items-center gap-3">
              {previewSrc && (
                <div className="relative">
                  <img
                    src={previewSrc}
                    alt="Logo preview"
                    className="w-14 h-14 object-contain border border-border rounded-sm bg-slate-50"
                  />
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="absolute -top-2 -right-2 w-5 h-5 bg-slate-900 text-white rounded-sm flex items-center justify-center"
                    aria-label="Remove logo"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <label className="inline-flex items-center gap-2 px-3 py-2 border border-border rounded-sm text-sm cursor-pointer hover:bg-slate-50">
                <ImageIcon className="w-4 h-4" />
                {previewSrc ? "Change" : "Upload"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                  data-testid="manufacturer-logo-input"
                />
              </label>
              {logoFile && <span className="text-xs text-muted-foreground">{logoFile.name}</span>}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Search mode</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {[
                {
                  value: "streamlined",
                  label: "Streamlined",
                  hint: "Type → pick spec sheet → add to cart",
                },
                {
                  value: "guided",
                  label: "Guided (AI wizard)",
                  hint: "Multi-step conversational filter",
                },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setForm({ ...form, search_mode: opt.value })}
                  className={`text-left rounded-sm border px-3 py-2 transition-colors ${
                    form.search_mode === opt.value
                      ? "border-amber-500 bg-amber-50 text-slate-900"
                      : "border-border bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid={`mfr-search-mode-${opt.value}`}
                >
                  <div className="text-sm font-semibold">{opt.label}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{opt.hint}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Notes</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1.5 rounded-sm"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800 rounded-sm"
              data-testid="manufacturer-save-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {mode === "edit" ? "Save changes" : "Create manufacturer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
